import { Icon } from '@iconify/react';
import { useQueries } from '@tanstack/react-query';
/**
 * Is anything waiting for me?
 *
 * That is the whole screen, and until now it was the one question Home did
 * not answer. It opened with four tiles reading 0, 0, 0 and 3/3, then a spend
 * card saying "no spend tracked yet", then a grid of everyone in the house
 * doing nothing in particular — a dashboard, of the kind that is looked at
 * once and never again. Meanwhile the things that genuinely needed him lived
 * elsewhere: a cash request had no screen at all, a late task had no button,
 * and the photograph the housekeeper took to prove the room was clean was in
 * the database and on no page.
 *
 * So: one line saying how many things need him, then those things, each one
 * finishable without leaving. When there is nothing, the queue disappears and
 * the screen says who is working and what is coming. Zero is silence, not a
 * tile with a nought in it.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ApiError,
  type Operation,
  type StaffRow,
  api,
  fmtMoney,
  fmtRelative,
  setCurrency,
} from '../api';
import { Avatar } from '../components/Avatar';
import { EnableNotifications } from '../components/EnableNotifications';
import { PromptModal } from '../components/Modal';
import { NotificationBell } from '../components/NotificationBell';
import { SkeletonScreen } from '../components/ui/Skeleton';
import { pickTranslation, t } from '../i18n';
import { get, invalidate } from '../query';

interface ReceiptProposalRow {
  id: string;
  aiSummary: string | null;
  proposal:
    | { kind: 'task'; title: string; description?: string | null; due_at?: string | null }
    | {
        kind: 'routine';
        title: string;
        description?: string | null;
        schedule_rrule: string;
        schedule_tz: string;
      }
    | null;
  uploadedByUserId: string;
  updatedAt: string;
}

interface PulseItem {
  id: string;
  kind: 'operation' | 'routine' | 'calendar';
  title: string;
  title_translations?: Record<string, string> | null;
  when: string;
  state?: string;
  assignee_user_id?: string | null;
}

/** The server row, camelCase off drizzle. `state` is the enum in money.ts:
 *  requested → approved → dispensed → reconciled. Nothing in the web app has
 *  ever read anything but `dispensed`. */
interface CashRequest {
  id: string;
  requestedByUserId: string;
  amountMinor: number;
  currency: string;
  purpose: string | null;
  state: 'requested' | 'approved' | 'rejected' | 'dispensed' | 'reconciled';
  createdAt: string;
}

interface Reimbursement {
  id: string;
  staffUserId: string;
  amountMinor: number;
  currency: string;
  note: string | null;
  state: 'submitted' | 'approved' | 'rejected' | 'paid';
  createdAt: string;
}

/** `text` is written by the detector and has always been on the row; this
 *  screen used to ignore it and switch on `kind` instead — against three kinds
 *  the server never emits, so every suggestion rendered as a raw enum. */
interface Suggestion {
  id: string;
  kind: string;
  text: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

interface LatestProof {
  operation_id: string;
  type: string;
  url: string | null;
  text_body: string | null;
  created_at: string | null;
}

interface Household {
  id: string;
  name: string;
  household_code: string;
  primary_currency: string;
  profile: { onboarded_at?: string };
}

interface MonthlyExpensesSnap {
  currency: string;
  total_minor: number;
  recent: Array<{ id: string }>;
}

export function OwnerHome() {
  const nav = useNavigate();
  const nowIso = new Date();
  const endOfDay = new Date(nowIso);
  endOfDay.setHours(23, 59, 59, 999);
  const pulseKey = `/v1/pulse?from=${encodeURIComponent(nowIso.toISOString())}&to=${encodeURIComponent(endOfDay.toISOString())}`;

  /**
   * Ten answers, each on its own.
   *
   * This used to be one `Promise.allSettled` behind a single `loaded` flag, so
   * a slow receipts call held the whole morning queue back and a failing one
   * was reported as the screen having failed. They are ten questions now: the
   * queue draws the moment the queue is known, and a section whose answer did
   * not arrive is simply the section that is missing.
   */
  const [opsQ, staffQ, pulseQ, sugQ, hhQ, rcptQ, cashQ, cashOkQ, reimbQ, monthQ] = useQueries({
    queries: [
      // No `tab` param — the server's "everything" view. Every task that
      // isn't yet approved/cancelled comes back in full no matter how old its
      // updated_at is, so a task scheduled for next week can't be silently
      // pushed out of the window by a busy day of approvals. Only the
      // approved/cancelled history is paginated (next_cursor/has_more); this
      // screen only reads recent completions off the digest, so it doesn't
      // page through history here.
      {
        queryKey: ['/v1/operations'],
        queryFn: get<{ items: Operation[]; next_cursor: string | null; has_more: boolean }>,
        refetchInterval: 30_000,
      },
      { queryKey: ['/v1/staff'], queryFn: get<{ items: StaffRow[] }> },
      { queryKey: [pulseKey], queryFn: get<{ items: PulseItem[] }> },
      { queryKey: ['/v1/suggestions'], queryFn: get<{ items: Suggestion[] }> },
      { queryKey: ['/v1/household'], queryFn: get<{ household: Household }> },
      { queryKey: ['/v1/receipts'], queryFn: get<{ items: ReceiptProposalRow[] }> },
      // Two states, two calls: money asked for in the shop, and money he has
      // already said yes to but not yet handed over. Both are things waiting
      // on him; neither had a screen.
      { queryKey: ['/v1/cash-requests?state=requested'], queryFn: get<{ items: CashRequest[] }> },
      { queryKey: ['/v1/cash-requests?state=approved'], queryFn: get<{ items: CashRequest[] }> },
      {
        queryKey: ['/v1/reimbursements?state=submitted'],
        queryFn: get<{ items: Reimbursement[] }>,
      },
      { queryKey: ['/v1/expenses/monthly'], queryFn: get<MonthlyExpensesSnap> },
    ],
  });

  const ops = opsQ.data?.items ?? [];
  const staff = staffQ.data?.items ?? [];
  const pulse = pulseQ.data?.items ?? [];
  const suggestions = sugQ.data?.items ?? [];
  const household = hhQ.data?.household ?? null;
  const receipts = rcptQ.data?.items ?? [];
  const cash = [...(cashQ.data?.items ?? []), ...(cashOkQ.data?.items ?? [])];
  const reimbursements = reimbQ.data?.items ?? [];
  const monthly = monthQ.data ?? null;
  const loaded = !opsQ.isPending && !staffQ.isPending;
  const err =
    (opsQ.error ?? staffQ.error)
      ? (opsQ.error ?? staffQ.error) instanceof ApiError
        ? ((opsQ.error ?? staffQ.error) as ApiError).message
        : t('Loading failed')
      : null;
  const refreshedAt = opsQ.dataUpdatedAt
    ? new Date(opsQ.dataUpdatedAt).toISOString()
    : nowIso.toISOString();

  // The photograph is the decision. One call for the whole queue rather than
  // one per card — and a failure here costs a thumbnail, not a row.
  const waitingIds = ops
    .filter((o) => o.state === 'waiting_approval')
    .slice(0, 20)
    .map((o) => o.id);
  const proofsQ = useQueries({
    queries: [
      {
        queryKey: [`/v1/operations/proofs/latest?ids=${waitingIds.join(',')}`],
        queryFn: get<{ items: LatestProof[] }>,
        enabled: waitingIds.length > 0,
      },
    ],
  })[0];
  const proofs = new Map((proofsQ?.data?.items ?? []).map((p) => [p.operation_id, p]));

  // Somebody who has not finished setting the house up has no queue to read.
  useEffect(() => {
    if (household && !household.profile?.onboarded_at) nav('/onboarding', { replace: true });
    if (household) setCurrency(household.primary_currency);
  }, [household, nav]);

  const load = () =>
    invalidate(
      '/v1/operations',
      '/v1/staff',
      '/v1/receipts',
      '/v1/cash-requests',
      '/v1/reimbursements',
      '/v1/suggestions',
      '/v1/expenses',
    );

  const staffById = new Map(staff.map((s) => [s.id, s]));
  const awaitingApproval = ops.filter((o) => o.state === 'waiting_approval');
  const late = ops.filter((o) => o.state === 'overdue' || o.state === 'escalated');
  const running = ops.filter((o) => o.state === 'in_progress' || o.state === 'waiting_proof');
  const activeStaff = staff.filter((s) => !s.disabled_at);

  /**
   * One line per person, and it must never be empty while work is out.
   *
   * This section was built from `in_progress` alone, which meant a house with
   * four tasks sent to three people and nobody having pressed Start yet — the
   * ordinary state of a morning — showed a blank screen under a status line
   * saying all was under control. Sent, seen, waiting for proof and scheduled
   * are all work that is out there; the row says who has it and what is next
   * for them.
   */
  const OPEN: string[] = ['sent', 'seen', 'in_progress', 'waiting_proof', 'scheduled'];
  const RANK: Record<string, number> = {
    in_progress: 0,
    waiting_proof: 1,
    seen: 2,
    sent: 3,
    scheduled: 4,
  };
  const openOps = ops
    .filter((o) => OPEN.includes(o.state))
    .sort((a, b) => (RANK[a.state] ?? 9) - (RANK[b.state] ?? 9));

  const digest: { person: StaffRow | null; current: Operation; next?: Operation }[] = [];
  const seenAssignee = new Set<string>();
  for (const op of openOps) {
    const key = op.assigneeUserId ?? 'unassigned';
    if (seenAssignee.has(key)) continue;
    seenAssignee.add(key);
    digest.push({
      person: op.assigneeUserId ? (staffById.get(op.assigneeUserId) ?? null) : null,
      current: op,
      next: openOps.find((o) => o.id !== op.id && (o.assigneeUserId ?? 'unassigned') === key),
    });
  }

  const decisions = awaitingApproval.length + cash.length + receipts.length + reimbursements.length;
  const waitingCount = decisions + late.length;
  const isEmpty = staff.length === 0 && ops.length === 0;

  if (!loaded) return <SkeletonScreen rows={4} />;

  return (
    // A phone column, centred. The owner shell is max-w-7xl because payroll
    // is a wide table; this screen is a stack of cards read one-handed, and
    // stretched to a laptop's width a proof photo becomes a mural.
    <div className="mx-auto w-full max-w-lg space-y-10">
      <StatusLine
        waiting={waitingCount}
        working={running.length}
        staffCount={activeStaff.length}
        next={pulse[0]}
        refreshedAt={refreshedAt}
        household={household}
      />

      {err && <div className="card border-urgent/10 bg-urgent/10 text-urgent">{err}</div>}

      <EnableNotifications />
      {isEmpty && <Quickstart />}

      {decisions > 0 && (
        <section className="space-y-4">
          <h2 className="section-title mb-0">{t('Requires decision')}</h2>

          {awaitingApproval.map((op) => (
            <ApprovalCard
              key={op.id}
              op={op}
              proof={proofs.get(op.id) ?? null}
              staffById={staffById}
              onChange={() => void load()}
            />
          ))}

          {cash.map((c) => (
            <CashCard key={c.id} row={c} staffById={staffById} onChange={() => void load()} />
          ))}

          {receipts.map((r) => (
            <ReceiptCard key={r.id} row={r} staffById={staffById} onChange={() => void load()} />
          ))}

          {reimbursements.map((r) => (
            <ReimbursementCard
              key={r.id}
              row={r}
              staffById={staffById}
              onChange={() => void load()}
            />
          ))}
        </section>
      )}

      {late.length > 0 && (
        <section className="space-y-4">
          <h2 className="section-title mb-0">{t('Needs attention')}</h2>
          {late.map((op) => (
            <LateCard key={op.id} op={op} staffById={staffById} onChange={() => void load()} />
          ))}
        </section>
      )}

      {suggestions.length > 0 && (
        <section className="space-y-4">
          <h2 className="section-title mb-0">{t('Worth a look')}</h2>
          {suggestions.map((s) => (
            <SuggestionCard key={s.id} s={s} onChange={() => void load()} />
          ))}
        </section>
      )}

      {digest.length > 0 && (
        <section className="space-y-4">
          <h2 className="section-title mb-0">
            {running.length > 0 ? t('Running now') : t('Today')}
          </h2>
          <div className="space-y-3">
            {digest.map((row) => (
              <PersonRow key={row.person?.id ?? 'unassigned'} row={row} />
            ))}
          </div>
        </section>
      )}

      {pulse.length > 0 && (
        <section className="space-y-6">
          <h2 className="section-title mb-0">{t("Today's timeline")}</h2>
          <div className="relative ms-2 space-y-8 border-s border-border ps-10">
            {pulse.slice(0, 8).map((item) => (
              <TimelineRow key={`${item.kind}-${item.id}`} item={item} staffById={staffById} />
            ))}
          </div>
        </section>
      )}

      <QuietLinks monthly={monthly} />
    </div>
  );
}

/**
 * One sentence, and it is the only thing on this screen that is always here.
 *
 * Four tiles reading zero told him nothing four times over. A number he can
 * act on gets colour and a pulse; a quiet house says so and stops talking.
 */
function StatusLine({
  waiting,
  working,
  staffCount,
  next,
  refreshedAt,
  household,
}: {
  waiting: number;
  working: number;
  staffCount: number;
  next?: PulseItem;
  refreshedAt: string;
  household: Household | null;
}) {
  const loud = waiting > 0;
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        {/* The house, as a tile. His design puts a photograph here; until one
            is uploaded this is the same shape holding the mark. */}
        <Link
          to="/memory"
          aria-label={household?.name ?? t('My household')}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm"
        >
          <Icon icon="ph:house-fill" className="text-lg" />
        </Link>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {loud && <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-urgent" />}
            <h1
              className={`truncate text-xl font-bold tracking-tight ${loud ? 'text-urgent' : 'text-foreground'}`}
            >
              {loud
                ? waiting === 1
                  ? t('One thing needs you.')
                  : t('{{n}} things need you.').replace('{{n}}', String(waiting))
                : t('All under control.')}
            </h1>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            <span>
              {working > 0
                ? t('{{n}} running now').replace('{{n}}', String(working))
                : t('{{n}} people').replace('{{n}}', String(staffCount))}
            </span>
            {next && (
              <>
                <span className="h-1 w-1 rounded-full bg-border" />
                <span className="truncate">
                  {t('Next')}: {pickTranslation(next.title_translations, next.title)}
                </span>
              </>
            )}
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>{fmtRelative(refreshedAt)}</span>
          </div>
        </div>
      </div>

      {/* The two things his header keeps on the right, and only on a phone —
          the laptop still has them in the bar above. */}
      <div className="flex shrink-0 items-center gap-1 sm:hidden">
        <NotificationBell />
        <Link
          to="/profile"
          aria-label={t('Profile')}
          className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-background bg-secondary text-foreground shadow-md transition-transform active:scale-95"
        >
          <Icon icon="ph:user-bold" className="text-lg" />
        </Link>
      </div>
    </header>
  );
}

/** Shared head of every queue card: who, when, and what it is about. */
function CardHead({
  person,
  when,
  title,
  tone = 'muted',
}: {
  person?: StaffRow | null;
  when: string;
  title: React.ReactNode;
  tone?: 'muted' | 'blue' | 'emerald';
}) {
  const toneClass =
    tone === 'blue' ? 'text-task' : tone === 'emerald' ? 'text-wealth' : 'text-muted-foreground';
  return (
    <div className="flex gap-4">
      {person && <Avatar name={person.full_name} userId={person.id} size="md" shape="square" />}
      <div className="min-w-0">
        <p className={`text-[10px] font-bold uppercase tracking-widest ${toneClass}`}>
          {person ? `${person.full_name} · ${when}` : when}
        </p>
        <h3 className="mt-1 text-lg font-bold leading-tight">{title}</h3>
      </div>
    </div>
  );
}

/**
 * The finished room, on the card.
 *
 * The proof has existed on every one of these tasks and been visible on none
 * of them — the links the app built pointed at `/r2/<key>`, which is not an
 * address. Approving a thing you cannot see is a rubber stamp, which is the
 * one thing this product exists to stop being.
 */
function ApprovalCard({
  op,
  proof,
  staffById,
  onChange,
}: {
  op: Operation;
  proof: LatestProof | null;
  staffById: Map<string, StaffRow>;
  onChange: () => void;
}) {
  const person = op.assigneeUserId ? staffById.get(op.assigneeUserId) : null;
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function decide(to: 'approved' | 'rejected', reason?: string) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/operations/${op.id}/transitions/${to}`, {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      });
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-4">
      <CardHead
        person={person}
        when={fmtRelative(op.updatedAt ?? op.createdAt ?? '')}
        title={
          <Link to={`/op/${op.id}`} dir="auto">
            {pickTranslation(op.titleTranslations, op.title)}
          </Link>
        }
      />

      {proof?.url && proof.type === 'photo' && (
        <Link
          to={`/op/${op.id}`}
          className="relative block aspect-[16/10] w-full overflow-hidden rounded-2xl bg-muted transition-transform active:scale-[0.98]"
        >
          <img src={proof.url} alt="" className="h-full w-full object-cover" />
        </Link>
      )}
      {proof?.url && proof.type === 'voice' && (
        <audio controls src={proof.url} className="w-full">
          <track kind="captions" />
        </audio>
      )}
      {proof?.text_body && (
        <p className="text-xs font-medium italic leading-relaxed text-foreground/80" dir="auto">
          “{proof.text_body}”
        </p>
      )}

      {err && <p className="text-xs font-bold text-urgent">{err}</p>}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={busy}
          onClick={() => void decide('approved')}
        >
          {t('Approve')}
        </button>
        <button
          type="button"
          className="btn-accent flex-1"
          disabled={busy}
          onClick={() => setRejecting(true)}
        >
          {t('Reject')}
        </button>
      </div>

      {rejecting && (
        <PromptModal
          title={t('Why reject?')}
          placeholder={t('Optional context for the team')}
          required={false}
          onSubmit={(reason: string) => void decide('rejected', reason)}
          onClose={() => setRejecting(false)}
        />
      )}
    </div>
  );
}

/**
 * Money asked for in the shop.
 *
 * The server has had `requested → approved → dispensed` since the beginning
 * and the app only ever fetched `dispensed` — a list of money already gone.
 * There has been no way, anywhere, to answer somebody standing at a till.
 */
function CashCard({
  row,
  staffById,
  onChange,
}: {
  row: CashRequest;
  staffById: Map<string, StaffRow>;
  onChange: () => void;
}) {
  const person = staffById.get(row.requestedByUserId);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);

  async function decide(decision: 'approve' | 'dispense' | 'reject', note?: string) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/cash-requests/${row.id}/decisions/${decision}`, {
        method: 'POST',
        body: JSON.stringify(note ? { note } : {}),
      });
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  // Already approved means the decision is made and the notes are not yet in
  // their hand — one button, and it says what it does.
  const approved = row.state === 'approved';

  return (
    <div className="card space-y-4">
      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-task/10 text-task">
          <Icon icon="ph:money-bold" className="text-2xl" />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-task">
            {t('Cash request')} · {person?.full_name ?? t('Someone')} · {fmtRelative(row.createdAt)}
          </p>
          <h3 className="mt-1 text-lg font-bold leading-tight">
            {fmtMoney(row.amountMinor, row.currency)}
            {row.purpose && (
              <span className="text-xs font-normal text-muted-foreground" dir="auto">
                {' '}
                {t('for')} {row.purpose}
              </span>
            )}
          </h3>
        </div>
      </div>

      {err && <p className="text-xs font-bold text-urgent">{err}</p>}

      <div className="flex gap-3">
        <button
          type="button"
          className="btn flex-[2] bg-task text-white"
          disabled={busy}
          onClick={() => void decide(approved ? 'dispense' : 'approve')}
        >
          {approved ? t('Mark as handed over') : t('Approve & dispense')}
        </button>
        <button
          type="button"
          className="btn-accent flex-1"
          disabled={busy}
          onClick={() => setRejecting(true)}
        >
          {t('Reject')}
        </button>
      </div>

      {rejecting && (
        <PromptModal
          title={t('Why reject?')}
          placeholder={t('Optional context for the team')}
          required={false}
          onSubmit={(note: string) => void decide('reject', note)}
          onClose={() => setRejecting(false)}
        />
      )}
    </div>
  );
}

function ReceiptCard({
  row,
  staffById,
  onChange,
}: {
  row: ReceiptProposalRow;
  staffById: Map<string, StaffRow>;
  onChange: () => void;
}) {
  const person = staffById.get(row.uploadedByUserId);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);

  async function decide(decision: 'approve' | 'reject', reason?: string) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/receipts/${row.id}/${decision}`, {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      });
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-4">
      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-wealth/10 text-wealth">
          <Icon icon="ph:receipt-bold" className="text-2xl" />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-wealth">
            {t('Receipt')} · {person?.full_name ?? t('Someone')} · {fmtRelative(row.updatedAt)}
          </p>
          <h3 className="mt-1 text-lg font-bold leading-tight" dir="auto">
            {row.proposal?.title ?? t('(empty proposal)')}
          </h3>
        </div>
      </div>

      {row.aiSummary && (
        <p className="text-xs font-medium leading-relaxed text-muted-foreground" dir="auto">
          {row.aiSummary}
        </p>
      )}

      {err && <p className="text-xs font-bold text-urgent">{err}</p>}

      <div className="flex gap-3">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={busy}
          onClick={() => void decide('approve')}
        >
          {t('Approve')}
        </button>
        <button
          type="button"
          className="btn-accent flex-1"
          disabled={busy}
          onClick={() => setRejecting(true)}
        >
          {t('Reject')}
        </button>
      </div>

      {rejecting && (
        <PromptModal
          title={t('Why reject?')}
          placeholder={t('Optional context for the team')}
          required={false}
          onSubmit={(reason: string) => void decide('reject', reason)}
          onClose={() => setRejecting(false)}
        />
      )}
    </div>
  );
}

function ReimbursementCard({
  row,
  staffById,
  onChange,
}: {
  row: Reimbursement;
  staffById: Map<string, StaffRow>;
  onChange: () => void;
}) {
  const person = staffById.get(row.staffUserId);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);

  async function decide(decision: 'approve' | 'reject', note?: string) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/reimbursements/${row.id}/decisions/${decision}`, {
        method: 'POST',
        body: JSON.stringify(note ? { note } : {}),
      });
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-4">
      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-task/10 text-task">
          <Icon icon="ph:hand-coins-bold" className="text-2xl" />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-task">
            {t('Paid out of pocket')} · {person?.full_name ?? t('Someone')} ·{' '}
            {fmtRelative(row.createdAt)}
          </p>
          <h3 className="mt-1 text-lg font-bold leading-tight">
            {fmtMoney(row.amountMinor, row.currency)}
            {row.note && (
              <span className="text-xs font-normal text-muted-foreground" dir="auto">
                {' '}
                {row.note}
              </span>
            )}
          </h3>
        </div>
      </div>

      {err && <p className="text-xs font-bold text-urgent">{err}</p>}

      <div className="flex gap-3">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={busy}
          onClick={() => void decide('approve')}
        >
          {t('Approve')}
        </button>
        <button
          type="button"
          className="btn-accent flex-1"
          disabled={busy}
          onClick={() => setRejecting(true)}
        >
          {t('Reject')}
        </button>
      </div>

      {rejecting && (
        <PromptModal
          title={t('Why reject?')}
          placeholder={t('Optional context for the team')}
          required={false}
          onSubmit={(note: string) => void decide('reject', note)}
          onClose={() => setRejecting(false)}
        />
      )}
    </div>
  );
}

/**
 * Late, with something to press.
 *
 * The app has known a task was late for a while — the worker marks it and
 * tells the person holding it. What it never had was a way for him to say
 * "any news?" without leaving for WhatsApp, which is the habit this product
 * exists to replace.
 */
function LateCard({
  op,
  staffById,
  onChange,
}: {
  op: Operation;
  staffById: Map<string, StaffRow>;
  onChange: () => void;
}) {
  const person = op.assigneeUserId ? staffById.get(op.assigneeUserId) : null;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function nudge() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/operations/${op.id}/nudge`, { method: 'POST', body: '{}' });
      setNote(t('They have been reminded.'));
      onChange();
    } catch (e) {
      // "We told them a minute ago" is not a failure, and it is usually the
      // lateness worker that told them — it stamps the same clock the moment
      // it marks a task late, which is exactly when he opens the app. Saying
      // it in red made the app look broken for doing the right thing.
      if (e instanceof ApiError && e.code === 'NUDGED_RECENTLY') {
        setNote(e.message);
      } else {
        setErr(e instanceof ApiError ? e.message : t('Failed'));
      }
    } finally {
      setBusy(false);
    }
  }

  const lateBy = op.dueAt ? fmtRelative(op.dueAt) : null;

  return (
    <div className="card border-s-4 border-s-magic space-y-4">
      <div className="flex gap-4">
        {person && (
          <span className="relative shrink-0">
            <Avatar name={person.full_name} userId={person.id} size="md" shape="square" />
            <span className="absolute -end-1 -top-1 h-4 w-4 rounded-full border-2 border-background bg-magic" />
          </span>
        )}
        <div className="min-w-0">
          <h3 className="text-lg font-bold leading-tight" dir="auto">
            <Link to={`/op/${op.id}`}>{pickTranslation(op.titleTranslations, op.title)}</Link>
          </h3>
          <p className="mt-0.5 text-sm font-medium text-magic">
            {person && lateBy
              ? t('{{name}} is late — due {{when}}')
                  .replace('{{name}}', person.full_name)
                  .replace('{{when}}', lateBy)
              : t('Past its deadline')}
          </p>
        </div>
      </div>

      {op.state === 'escalated' && (
        <div className="rounded-2xl bg-magic/10/60 p-3">
          <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-magic">
            {t('Suggested action')}
          </p>
          <p className="text-xs font-medium leading-relaxed text-magic">
            {t('Nobody has picked this up. Ask for a status or hand it to someone else.')}
          </p>
        </div>
      )}

      {note && <p className="text-xs font-semibold text-muted-foreground">{note}</p>}
      {err && <p className="text-xs font-bold text-urgent">{err}</p>}

      <div className="flex gap-2">
        <button
          type="button"
          className="btn flex-1 bg-magic text-white"
          disabled={busy || !person}
          onClick={() => void nudge()}
        >
          {person ? t('Nudge {{name}}').replace('{{name}}', person.full_name) : t('Nudge')}
        </button>
        <Link
          to={`/op/${op.id}`}
          className="flex w-14 items-center justify-center rounded-2xl bg-secondary text-secondary-foreground"
          aria-label={t('Open')}
        >
          <Icon icon="ph:chats-bold" className="text-xl" />
        </Link>
      </div>
    </div>
  );
}

/**
 * The row already carries the sentence a person should read. This screen used
 * to ignore it and switch on `kind` — against `overdue_streak`,
 * `routine_skip_streak` and `long_running`, none of which the server has ever
 * emitted, so every suggestion rendered as the raw enum name.
 */
/**
 * The detector's sentence says what happened; the kind says what sort of thing
 * it is. A title and an icon above the sentence is how "delegate work" or
 * "unusual spend" reads as a category rather than one more line of prose.
 * Unknown kinds fall back to the generic sparkle with no title.
 */
const SUGGESTION_META: Record<string, { icon: string; label: string }> = {
  unapproved_receipts: { icon: 'ph:receipt-bold', label: 'Receipts to review' },
  cash_lingering: { icon: 'ph:money-bold', label: 'Unreconciled cash' },
  loan_inactivity: { icon: 'ph:hand-coins-bold', label: 'Dormant loan' },
  unused_memory: { icon: 'ph:brain-bold', label: 'Unused note' },
  delegation_opportunity: { icon: 'ph:users-three-bold', label: 'Delegate work' },
  spend_anomaly: { icon: 'ph:chart-line-up-bold', label: 'Unusual spend' },
};

function SuggestionCard({ s, onChange }: { s: Suggestion; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const meta = SUGGESTION_META[s.kind];
  async function dismiss() {
    setBusy(true);
    try {
      await api(`/v1/suggestions/${s.id}/dismiss`, { method: 'POST', body: '{}' });
      onChange();
    } catch {
      setBusy(false);
    }
  }
  return (
    <div className="card flex items-start gap-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-secondary text-muted-foreground">
        <Icon icon={meta?.icon ?? 'ph:sparkle-fill'} className="text-lg" />
      </span>
      <div className="min-w-0 flex-1">
        {meta && (
          <p className="mb-0.5 text-[10px] font-bold uppercase tracking-widest text-task">
            {t(meta.label)}
          </p>
        )}
        <p className="text-sm font-medium leading-relaxed" dir="auto">
          {s.text ?? s.kind}
        </p>
      </div>
      <button
        type="button"
        className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
        disabled={busy}
        onClick={() => void dismiss()}
      >
        {t('Dismiss')}
      </button>
    </div>
  );
}

/**
 * Who has what, right now.
 *
 * The state is said in words rather than left to a colour: "started" and
 * "sent, not opened yet" are different facts about the morning and the second
 * one is the one that turns into a phone call at eleven.
 */
function PersonRow({
  row,
}: {
  row: { person: StaffRow | null; current: Operation; next?: Operation };
}) {
  const { person, current, next } = row;
  const started = current.state === 'in_progress' || current.state === 'waiting_proof';
  const dot = started ? 'bg-task' : current.state === 'scheduled' ? 'bg-border' : 'bg-wealth';
  const stateWord =
    current.state === 'in_progress'
      ? t('Started')
      : current.state === 'waiting_proof'
        ? t('Waiting for proof')
        : current.state === 'seen'
          ? t('Opened it')
          : current.state === 'scheduled'
            ? t('Scheduled')
            : t('Sent, not opened yet');

  return (
    <Link
      to={`/op/${current.id}`}
      className="flex w-full items-center justify-between gap-3 rounded-card border border-border bg-card p-5 transition-all active:scale-[0.99]"
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <span className="relative shrink-0">
          {person ? (
            <Avatar name={person.full_name} userId={person.id} size="md" />
          ) : (
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-muted-foreground">
              <Icon icon="ph:user-bold" />
            </span>
          )}
          <span
            className={`absolute bottom-0 end-0 h-3 w-3 rounded-full border-2 border-background ${dot}`}
          />
        </span>
        {/* One column, not two. A phone is 390px wide and the second column
            was taking a third of it to say "Next", which cut every task title
            in half — the one thing on the row you actually read. */}
        <div className="min-w-0 flex-1 text-start">
          <div className="flex items-baseline justify-between gap-2">
            <h4 className="truncate text-sm font-bold">{person?.full_name ?? t('Unassigned')}</h4>
            <span className="shrink-0 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
              {stateWord}
            </span>
          </div>
          <p className="mt-1 truncate text-sm font-semibold" dir="auto">
            {pickTranslation(current.titleTranslations, current.title)}
          </p>
          {next && (
            <p className="mt-1 truncate text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {t('Next')} · {pickTranslation(next.titleTranslations, next.title)}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

function TimelineRow({
  item,
  staffById,
}: {
  item: PulseItem;
  staffById: Map<string, StaffRow>;
}) {
  const person = item.assignee_user_id ? staffById.get(item.assignee_user_id) : null;
  const to =
    item.kind === 'calendar'
      ? `/event/${item.id}`
      : item.kind === 'operation'
        ? `/op/${item.id}`
        : '/tasks';
  return (
    <div className="relative">
      <span className="absolute -start-[37px] top-1 h-2.5 w-2.5 rounded-full bg-border" />
      <Link to={to} className="flex flex-col">
        <span className="text-[10px] font-bold tracking-widest text-muted-foreground">
          {fmtRelative(item.when)}
        </span>
        <span className="text-sm font-bold" dir="auto">
          {pickTranslation(item.title_translations, item.title)}
        </span>
        {person && (
          <span className="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {person.full_name}
          </span>
        )}
      </Link>
    </div>
  );
}

/** The things he goes to when nothing is asking for him. An amount only when
 *  there is one — a "0" here is the same noise the tiles were. */
function QuietLinks({ monthly }: { monthly: MonthlyExpensesSnap | null }) {
  const spend =
    monthly && monthly.recent.length > 0 ? fmtMoney(monthly.total_minor, monthly.currency) : null;
  return (
    <section className="flex items-center justify-between border-t border-border px-1 pb-10 pt-10">
      <Link
        to="/memory"
        className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
      >
        {t('House rules')}
      </Link>
      <Link
        to="/staff"
        className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
      >
        {t('People')}
      </Link>
      <Link
        to="/money"
        className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
      >
        {spend ? `${t('Money')} · ${spend}` : t('Money')}
      </Link>
    </section>
  );
}

/** Only ever seen once, by a household with nothing in it yet. */
function Quickstart() {
  const cards: { to: string; title: string; icon: string }[] = [
    { to: '/staff', title: t('Add your first staff member'), icon: 'ph:user-plus-bold' },
    { to: '/tasks?new=1', title: t('Create first household task'), icon: 'ph:sparkle-fill' },
    { to: '/routines', title: t('Add a routine'), icon: 'ph:clock-counter-clockwise-bold' },
    { to: '/memory', title: t('Add a rule'), icon: 'ph:book-open-bold' },
  ];
  return (
    <section className="space-y-4">
      <h2 className="section-title mb-0">{t('Quickstart')}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {cards.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="card flex items-center gap-4 transition-all active:scale-[0.99]"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-secondary">
              <Icon icon={c.icon} className="text-2xl" />
            </span>
            <span className="font-bold leading-5">{c.title}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
